import { useCallback, useEffect, useMemo, useState } from 'react';
import { Info, SlidersHorizontal, Tag } from '@phosphor-icons/react';
import { toast } from 'sonner';

import { AdjustCategoryBudgetDialog } from '../../components/budget/AdjustCategoryBudgetDialog';
import { BudgetAllocationHero } from '../../components/budget/BudgetAllocationHero';
import { BudgetByGroupList } from '../../components/budget/BudgetByGroupList';
import {
  BudgetCategoryFocusPanel,
  resolveCategoryId,
} from '../../components/budget/BudgetCategoryFocusPanel';
import { BudgetCycleIncomeSection } from '../../components/budget/BudgetCycleIncomeSection';
import { BudgetMethodSheet } from '../../components/budget/BudgetMethodSheet';
import { BudgetSpendCycleLink } from '../../components/budget/BudgetSpendCycleLink';
import { groupKey, splitGroupCategories } from '../../components/budget/budgetGroupCategories';
import { isFirstCycleWithoutHistory } from '../../components/budget/categoryBudgetSuggestionUtils';
import { CategoryFormDialog } from '../../components/categories/CategoryFormDialog';
import { EmptyState } from '../../components/common/EmptyState';
import { PageContentShell } from '../../components/common/PageContentShell';
import { SectionSkeleton } from '../../components/common/SectionSkeleton';
import { Button } from '../../components/ui/Button';
import { cn } from '../../utils/cn';
import { formatEUR } from '../../utils/money';
import {
  buildAllocationSegments,
  allocationCompletionHint,
} from '../../components/budget/budgetAllocationUtils';
import { useBudgetOverviewQuery } from '../../features/budgets/queries';
import { useCreateCategoryMutation } from '../../features/expenses/queries';
import { buildCycleOverviewQueryArgs } from '../../hooks/useCycleFinancialOverview';
import { useSelfieCycle } from '../../store/SelfieCycleContext';
import { useMobilePageHeaderTrailing } from '../../store/MobilePageHeaderContext';
import { allocationForBudgetGroup } from '../../utils/categoryAllocation';
import pt from '../../locales/pt';
import { useQueryShellState } from '../../hooks/useQueryShellState';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import { GuidedFlowReturnBanner } from '../../components/dashboard/GuidedFlowReturnBanner';

function BudgetDesktopInsightCard({ overview }) {
  const income = Number(overview?.cycle_income_eur ?? 0);
  if (!overview?.has_income || income <= 0) return null;

  const groups = overview.groups || [];
  const { totalAssigned } = buildAllocationSegments({ groups, cycleIncomeEur: income });
  const { state, remaining, excess } = allocationCompletionHint({ totalAssigned, income });

  if (state === 'complete') return null;

  const isOver = state === 'over';
  const amount = isOver ? excess : remaining;

  return (
    <div
      className={cn(
        'flex w-[380px] shrink-0 items-start gap-space-3 rounded-ds-lg p-space-4',
        isOver ? 'bg-warning-tint' : 'bg-sage-tint',
      )}
    >
      <div
        className={cn(
          'flex h-8 w-8 shrink-0 items-center justify-center rounded-ds-sm',
          isOver ? 'bg-warning-500/20' : 'bg-sage-500/20',
        )}
      >
        <Info
          className={cn('h-[18px] w-[18px]', isOver ? 'text-warning-fg' : 'text-sage-on-tint')}
          aria-hidden
        />
      </div>
      <div className="flex min-w-0 flex-1 flex-col gap-space-1">
        <p className={cn('type-body font-semibold leading-[1.35]', isOver ? 'text-warning-fg' : 'text-sage-on-tint')}>
          {isOver
            ? `${formatEUR(amount ?? 0)} acima do rendimento`
            : `Ainda tens ${formatEUR(amount ?? 0)} por atribuir`}
        </p>
        <p className="type-body-sm font-normal text-foreground">
          {isOver
            ? pt.budget.allocationHero.hintOver.replace('{amount}', formatEUR(excess ?? 0))
            : 'Dá um destino a cada euro antes do ciclo fechar.'}
        </p>
      </div>
    </div>
  );
}

function BudgetMethodBadge({ methodName, onClick, disabled, className }) {
  if (!methodName) return null;

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'type-label inline-flex items-center gap-space-2 font-semibold text-sage-on-tint transition-colors hover:text-sage-700 disabled:opacity-50',
        className,
      )}
      aria-label={pt.budget.methodBadge.replace('{name}', methodName)}
    >
      <Info className="h-3.5 w-3.5 shrink-0" aria-hidden />
      <span className="truncate">{pt.budget.methodBadge.replace('{name}', methodName)}</span>
    </button>
  );
}

export function Budget() {
  const { currentCycle: activeCycle, isLoading: cyclesLoading } = useSelfieCycle();
  const overviewArgs = useMemo(
    () => buildCycleOverviewQueryArgs(activeCycle, { includeArchivedWithSpending: false }),
    [activeCycle],
  );
  const isMd = useMediaQuery('(min-width: 768px)');
  const isLg = useMediaQuery('(min-width: 1024px)');
  const layout = isLg ? 'desktop' : isMd ? 'tablet' : 'mobile';
  const focusPlacement = layout === 'mobile' ? 'inline' : 'side';

  const [adjusting, setAdjusting] = useState(null);
  const [methodSheetOpen, setMethodSheetOpen] = useState(false);
  const [categoryFormOpen, setCategoryFormOpen] = useState(false);
  const [createForGroup, setCreateForGroup] = useState(null);
  const [focus, setFocus] = useState(null);

  const createCategory = useCreateCategoryMutation();

  const { data: overview, isPending: overviewPending, isFetching: overviewFetching } =
    useBudgetOverviewQuery(overviewArgs);

  const overviewShell = useQueryShellState({
    data: overviewArgs.enabled ? overview : cyclesLoading ? undefined : {},
    isPending: cyclesLoading || (overviewArgs.enabled && overviewPending),
    isFetching: overviewArgs.enabled && overviewFetching,
  });

  const groups = useMemo(() => overview?.groups || [], [overview]);
  const showGroupMetrics = Boolean(overview?.show_group_metrics ?? overview?.has_income);
  const canEditBudgets = Boolean(overview?.can_edit_budgets ?? true);
  const canAddCategory = Boolean(overview?.method) && canEditBudgets;
  const methodName = overview?.method?.name;
  const firstCycle = useMemo(
    () => Boolean(overview?.method) && isFirstCycleWithoutHistory(groups),
    [overview?.method, groups],
  );

  const openMethodSheet = useCallback(() => {
    if (canEditBudgets) setMethodSheetOpen(true);
  }, [canEditBudgets]);

  useMobilePageHeaderTrailing(
    methodName
      ? {
          label: pt.budget.methodBadge.replace('{name}', methodName),
          onClick: openMethodSheet,
          ariaLabel: pt.budget.method.changeTitle,
        }
      : null,
  );

  const showPlanningIncomeSection = Boolean(
    overview?.method && !overview?.is_historical_cycle,
  );

  const cycleHeading = firstCycle
    ? pt.budget.firstCycleTitle
    : activeCycle?.label
      ? pt.budget.cycleTitle.replace('{label}', activeCycle.label)
      : pt.budget.title;

  const focusedGroup = useMemo(() => {
    if (!focus?.groupKey) return null;
    return groups.find((g) => groupKey(g) === focus.groupKey) || null;
  }, [focus, groups]);

  const focusedCategories = useMemo(() => {
    if (!focusedGroup) return { category: null, siblings: [] };
    const cats = splitGroupCategories(focusedGroup);
    const category = cats.find((c) => resolveCategoryId(c) === focus.categoryId) || null;
    const siblings = category
      ? cats.filter((c) => resolveCategoryId(c) !== focus.categoryId)
      : [];
    return { category, siblings };
  }, [focusedGroup, focus]);

  useEffect(() => {
    if (!focus) return;
    if (!focusedCategories.category) setFocus(null);
  }, [focus, focusedCategories.category]);

  const openCreateCategory = (group = null) => {
    setCreateForGroup(group);
    setCategoryFormOpen(true);
  };

  const handleCreateCategory = async (values) => {
    try {
      await createCategory.mutateAsync(values);
      toast.success('Categoria criada.');
      setCategoryFormOpen(false);
      setCreateForGroup(null);
    } catch (err) {
      toast.error(
        typeof err === 'string'
          ? err
          : err?.detail || err?.name || 'Erro ao guardar.',
      );
    }
  };

  const categoryFormDefaults = useMemo(() => {
    if (!createForGroup) return undefined;
    const allocation = allocationForBudgetGroup(createForGroup);
    return {
      budget_allocation: allocation,
      is_essential: allocation === 'essential',
    };
  }, [createForGroup]);

  const handleFocusChange = useCallback((next) => {
    setFocus(next);
  }, []);

  const handleSelectSibling = useCallback(
    (category) => {
      if (!focusedGroup) return;
      const catId = resolveCategoryId(category);
      if (!catId) return;
      setFocus({ groupKey: groupKey(focusedGroup), categoryId: catId });
    },
    [focusedGroup],
  );

  const groupsBlock = (
    <BudgetPlanningGroups
      groups={groups}
      hasMethod={Boolean(overview?.method)}
      showGroupMetrics={showGroupMetrics}
      canEditBudgets={canEditBudgets}
      canAddCategory={canAddCategory}
      onAdjustDialog={setAdjusting}
      onCreateCategory={openCreateCategory}
      firstCycle={firstCycle}
      focusPlacement={focusPlacement}
      focusedCategoryId={focus?.categoryId ?? null}
      focusedGroupKey={focus?.groupKey ?? null}
      onFocusChange={handleFocusChange}
    />
  );

  const contentFallback = (
    <div className="flex flex-col gap-4 md:gap-6">
      <SectionSkeleton variant="hero" />
      <SectionSkeleton variant="card-grid" />
    </div>
  );

  const heroBlock = (
    <>
      {overview ? (
        <BudgetAllocationHero
          overview={overview}
          firstCycle={firstCycle}
          activeCycle={activeCycle}
        />
      ) : null}

      {overview && showPlanningIncomeSection ? (
        <BudgetCycleIncomeSection overview={overview} canEditBudgets={canEditBudgets} />
      ) : null}

      {!overview?.method ? (
        <Button type="button" variant="outline" onClick={() => setMethodSheetOpen(true)}>
          {pt.budget.method.cta}
        </Button>
      ) : null}
    </>
  );

  const showSideFocus =
    focusPlacement === 'side'
    && focusedGroup
    && focusedCategories.category;

  const volanteColumn = (
    <div className="flex min-w-0 flex-col gap-4 md:gap-6">
      {heroBlock}
      {groupsBlock}
      {overview?.method ? <BudgetSpendCycleLink /> : null}
    </div>
  );

  const focusColumn = showSideFocus ? (
    <BudgetCategoryFocusPanel
      group={focusedGroup}
      category={focusedCategories.category}
      siblings={focusedCategories.siblings}
      canEditBudgets={canEditBudgets}
      onAdjustDialog={setAdjusting}
      onBack={() => setFocus(null)}
      onSelectSibling={handleSelectSibling}
      className="sticky top-4"
    />
  ) : null;

  return (
    <div className="flex w-full min-w-0 flex-col gap-4 overflow-x-hidden md:gap-6">
      <GuidedFlowReturnBanner />
      <header className="flex items-center justify-between gap-space-3">
        <div className="min-w-0">
          <p className="type-section text-sage-on-tint">
            {pt.budget.eyebrow}
          </p>
          <h1 className="type-title-page tracking-tight">
            {cycleHeading}
          </h1>
          <p className="type-body-sm hidden font-normal text-muted-foreground md:block">{pt.budget.subtitle}</p>
        </div>
        {methodName ? (
          <BudgetMethodBadge
            methodName={methodName}
            onClick={openMethodSheet}
            disabled={!canEditBudgets}
            className="hidden md:inline-flex"
          />
        ) : (
          <Button
            type="button"
            variant="outline"
            className="hidden md:inline-flex"
            onClick={() => setMethodSheetOpen(true)}
          >
            {pt.budget.method.cta}
          </Button>
        )}
      </header>

      <PageContentShell shells={[overviewShell]} fallback={contentFallback}>
        {showSideFocus && layout === 'desktop' ? (
          <div
            className="grid grid-cols-[minmax(0,1fr)_455px] items-start gap-space-5"
            data-testid="budget-layout-desktop"
          >
            {volanteColumn}
            {focusColumn}
          </div>
        ) : showSideFocus && layout === 'tablet' ? (
          <div
            className="grid grid-cols-2 items-start gap-space-4"
            data-testid="budget-layout-tablet"
          >
            {volanteColumn}
            {focusColumn}
          </div>
        ) : layout === 'desktop' ? (
          <div className="flex flex-col gap-space-5" data-testid="budget-layout-desktop">
            <div className="flex items-start gap-space-5">
              <div className="flex min-w-0 flex-1 flex-col gap-space-4">
                {heroBlock}
              </div>
              {overview ? <BudgetDesktopInsightCard overview={overview} /> : null}
            </div>
            <BudgetPlanningGroups
              groups={groups}
              hasMethod={Boolean(overview?.method)}
              showGroupMetrics={showGroupMetrics}
              canEditBudgets={canEditBudgets}
              canAddCategory={canAddCategory}
              onAdjustDialog={setAdjusting}
              onCreateCategory={openCreateCategory}
              firstCycle={firstCycle}
              focusPlacement="side"
              focusedCategoryId={focus?.categoryId ?? null}
              focusedGroupKey={focus?.groupKey ?? null}
              onFocusChange={handleFocusChange}
              layout="desktop"
            />
            {overview?.method ? <BudgetSpendCycleLink /> : null}
          </div>
        ) : (
          <div
            className="flex min-w-0 flex-col gap-4 md:gap-6"
            data-testid={layout === 'tablet' ? 'budget-layout-tablet' : 'budget-layout-mobile'}
          >
            {volanteColumn}
          </div>
        )}
      </PageContentShell>

      <AdjustCategoryBudgetDialog
        isOpen={Boolean(adjusting)}
        onClose={() => setAdjusting(null)}
        category={adjusting}
        currentBudget={adjusting?.budget_eur ?? null}
      />

      <CategoryFormDialog
        isOpen={categoryFormOpen}
        onClose={() => {
          setCategoryFormOpen(false);
          setCreateForGroup(null);
        }}
        onSubmit={handleCreateCategory}
        initialValues={categoryFormDefaults}
        isPending={createCategory.isPending}
        createTitle={pt.budget.categories.newExpenseCategory}
        createDescription={
          createForGroup?.name
            ? pt.budget.categories.formCreateDescription.replace(
                '{group}',
                createForGroup.name,
              )
            : pt.budget.categories.formCreateDescriptionGeneric
        }
      />

      <BudgetMethodSheet isOpen={methodSheetOpen} onClose={() => setMethodSheetOpen(false)} />
    </div>
  );
}

function BudgetPlanningGroups({
  groups,
  hasMethod = false,
  onAdjustDialog,
  showGroupMetrics,
  canEditBudgets,
  canAddCategory,
  onCreateCategory,
  firstCycle,
  focusPlacement,
  focusedCategoryId,
  focusedGroupKey,
  onFocusChange,
  layout,
}) {
  if (!groups || groups.length === 0) {
    return (
      <div className="flex flex-col gap-space-5">
        <EmptyState
          icon={hasMethod ? Tag : SlidersHorizontal}
          title={pt.budget.method.empty}
          description={pt.budget.subtitle}
          action={
            canAddCategory ? (
              <Button type="button" onClick={() => onCreateCategory(null)}>
                <Tag className="mr-1 h-4 w-4" aria-hidden />
                {pt.budget.categories.newExpenseCategory}
              </Button>
            ) : null
          }
        />
        {!hasMethod ? (
          <div className="flex items-start gap-space-3 rounded-ds-lg bg-sage-tint p-space-4">
            <Info className="h-[18px] w-[18px] shrink-0 text-sage-on-tint" aria-hidden />
            <div className="flex min-w-0 flex-1 flex-col gap-space-1">
              <p className="type-body font-semibold leading-[1.35] text-sage-on-tint">
                {pt.budget.method.explainerTitle}
              </p>
              <p className="type-body-sm font-normal leading-[1.45] text-foreground">
                {pt.budget.method.explainerDescription}
              </p>
            </div>
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <BudgetByGroupList
      groups={groups}
      hasIncome={showGroupMetrics}
      canEditBudgets={canEditBudgets}
      canAddCategory={canAddCategory}
      onAdjustDialog={onAdjustDialog}
      onCreateCategory={onCreateCategory}
      firstCycle={firstCycle}
      focusPlacement={focusPlacement}
      focusedCategoryId={focusedCategoryId}
      focusedGroupKey={focusedGroupKey}
      onFocusChange={onFocusChange}
      layout={layout}
    />
  );
}
